import React, { useEffect, useState } from "react";
import { HiOutlineBellAlert } from "react-icons/hi2";
import ebenchLogo from "../../images/ebench_logo.png";
import { RxHamburgerMenu } from "react-icons/rx";
import { useAdminById } from "../../Hooks/useAdmin";
import {
  GetAllNotification,
  GetUnreadNotificationCount,
  ViewNotification,
} from "../../Services/notificationServices";
import {
  connectWebSocket,
  disconnectWebSocket,
} from "../../utils/websocketService";
import { useDispatch, useSelector } from "react-redux";
import { FiChevronDown } from "react-icons/fi";
import { logoutUser } from "../../Services/authService";
import { persistor } from "../../Redux/store";
import { SIGNOUT_USER_SUCCESS } from "../../Redux/constants/AuthConstant";
import { useNavigate } from "react-router-dom";

const Header = ({ toggleSidebar }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [width, setWidth] = useState(window.innerWidth);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationList, setNotificationList] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { userResult } = useSelector((state) => state?.Auth);
  const adminId = userResult?.adminid || localStorage.getItem("adminid");
  const { data } = useAdminById(adminId);

  const menuItemStyle = {
    padding: "10px 14px",
    cursor: "pointer",
    fontSize: "0.9rem",
    color: "#1f2937",
    transition: "0.2s",
    userSelect: "none",
    borderRadius: "6px",
  };

  useEffect(() => {
    if (!adminId) return;

    const payload = { id: adminId, role: "ADMIN" };

    const fetchNotifications = async () => {
      try {
        const res = await GetAllNotification(payload);
        setNotificationList(res?.data?.data || []);
        const unreadRes = await GetUnreadNotificationCount(payload);
        setUnreadCount(unreadRes?.data?.unreadCount || 0);
      } catch (error) {
        console.error("Error fetching admin notifications:", error);
      }
    };

    fetchNotifications();

    connectWebSocket(
      payload.role,
      payload.id,
      (newNotification) => {
        if (!newNotification) return;
        setNotificationList((prev) => [newNotification, ...prev]);
        setUnreadCount((prev) => prev + 1);
      },
      (unread) => setUnreadCount(unread || 0)
    );

    return () => disconnectWebSocket();
  }, [adminId]);

  const handleViewNotification = () => {
    const data = { id: adminId, role: "ADMIN" };
    ViewNotification(data).then(() => setUnreadCount(0));
    GetAllNotification(data).then((res) => setNotificationList(res.data.data));
  };

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowNotifications(false);
      setShowProfileMenu(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const logoutHandler = () => {
    const userId = userResult?.adminid || localStorage.getItem("adminid");
    const userType = userResult.usertype || localStorage.getItem("usertype");
    logoutUser(parseInt(userId), userType).then(() => {
      navigate("/admin-login");
      setTimeout(() => {
        localStorage.clear();
        persistor.purge();
        dispatch({
          type: SIGNOUT_USER_SUCCESS,
          payload: {},
          isSigninIn: false,
          msg: "logout successfully",
        });
      }, 400);
    });
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "Invalid date";
    const now = new Date();
    const created = new Date(Number(timestamp));
    if (isNaN(created.getTime())) return "Invalid date";
    const diffInSeconds = Math.floor((now - created) / 1000);
    if (diffInSeconds < 60)
      return `${diffInSeconds} sec${diffInSeconds !== 1 ? "s" : ""} ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60)
      return `${diffInMinutes} min${diffInMinutes !== 1 ? "s" : ""} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hr${diffInHours !== 1 ? "s" : ""} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  };

 return (
  <div
    style={{
      width: "100%",
      borderBottom: "1px solid #1f4c6b",
      backgroundColor: "#005792",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      position: "sticky",
      top: 0,
      zIndex: 1000,
    }}
  >
    <div
      style={{
        width: "95%",
        margin: "0 auto",
        height: width < 480 ? "4rem" : "5rem", // Responsive height
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: width < 768 ? "0 4px" : "0",
      }}
    >
      {/* LEFT SECTION */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: width < 480 ? "0.5rem" : "1rem",
        }}
      >
        {/* Logo Box */}
        <div
          style={{
            background: "#fff",
            padding: width < 480 ? "0.2rem 0.4rem" : "0.25rem 0.5rem",
            borderRadius: "4px",
          }}
        >
          <img
            src={ebenchLogo}
            alt="Ebench Logo"
            style={{
              height: width < 480 ? "1.6rem" : "2rem",
              objectFit: "contain",
            }}
          />
        </div>

        {/* Admin Panel Badge */}
        {width > 480 && (
          <span
            style={{
              fontWeight: "600",
              fontSize: "0.75rem",
              color: "#fff",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              padding: "2px 8px",
              borderRadius: "12px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              backdropFilter: "blur(3px)",
            }}
          >
            Admin Panel
          </span>
        )}

        {/* Hamburger Menu (mobile only) */}
        <div
          onClick={toggleSidebar}
          style={{
            display: width > 1024 ? "none" : "block",
            cursor: "pointer",
          }}
        >
          <RxHamburgerMenu
            style={{
              fontSize: width < 480 ? "1.3rem" : "1.5rem",
              color: "#fff",
            }}
          />
        </div>
      </div>

      {/* 🔔 RIGHT SECTION */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: width < 480 ? "0.8rem" : "1.5rem",
        }}
      >
        {/* Notifications */}
        <div className="position-relative">
          <HiOutlineBellAlert
            style={{
              fontSize: width < 480 ? "1.3rem" : "1.6rem",
              color: "#ffeb3b",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowNotifications((prev) => !prev);
              handleViewNotification();
            }}
          />

          {/* Badge */}
          {unreadCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: "-6px",
                right: "-6px",
                background: "#ef4444",
                color: "#fff",
                borderRadius: "50%",
                width: width < 480 ? "14px" : "18px",
                height: width < 480 ? "14px" : "18px",
                fontSize: width < 480 ? "9px" : "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "600",
              }}
            >
              {unreadCount}
            </span>
          )}

          {/* Notification dropdown */}
          {showNotifications && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                right: "-10px",
                top: width < 480 ? "25px" : "30px",
                width: "250px",
                maxHeight: "75vh",
                overflowY: "auto",
                background: "#fff",
                borderRadius: "8px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                padding: "10px",
                zIndex: 999,
              }}
            >
              <h6
                style={{
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#286a94",
                }}
              >
                Admin Notifications
              </h6>

              {notificationList?.length > 0 ? (
                notificationList.map((n) => (
                  <div
                    key={n?.notificationId}
                    style={{
                      borderBottom: "1px solid #e5e7eb",
                      paddingBottom: "6px",
                      marginBottom: "6px",
                      fontSize: "14px",
                      color: "#374151",
                    }}
                  >
                    🔔 {n?.message}
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {getTimeAgo(n?.createdDate)}
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ fontSize: "14px", color: "#6b7280" }}>
                  No new notifications
                </p>
              )}
            </div>
          )}
        </div>

        {/* 👤 PROFILE SECTION */}
        <div
          style={{ position: "relative", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            setShowProfileMenu((prev) => !prev);
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <div
              style={{
                width: width < 480 ? "2.2rem" : "3rem",
                height: width < 480 ? "2.2rem" : "3rem",
                borderRadius: "50%",
                overflow: "hidden",
                border: "2px solid #fff",
              }}
            >
              <img
                src="https://img.freepik.com/free-photo/handsome-bearded-guy-posing-against-white-wall_273609-20597.jpg"
                alt="Profile"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>

            {/* Hide text on very small screens */}
            {width > 600 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  fontSize: "0.875rem",
                  color: "#ffffff",
                }}
              >
                <span
                  style={{
                    fontWeight: "600",
                    fontSize: "0.9rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "120px",
                  }}
                >
                  {data?.data?.name}
                </span>

                <span
                  style={{
                    fontSize: "0.75rem",
                    marginTop: "2px",
                    color: "rgba(255, 255, 255, 0.85)",
                  }}
                >
                  {userResult?.email}
                </span>
              </div>
            )}

            {/* Arrow icon */}
            {width > 480 && (
              <FiChevronDown
                size={18}
                style={{
                  color: "#fff",
                  transition: "0.25s",
                  transform: showProfileMenu
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                }}
              />
            )}
          </div>

          {/* Profile Dropdown */}
          {showProfileMenu && (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: width < 480 ? "3rem" : "4rem",
                width: "180px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                zIndex: 999,
                padding: "8px 0",
              }}
            >
              <div style={menuItemStyle}>Profile</div>
              <div style={menuItemStyle}>Settings</div>
              <div
                style={{
                  ...menuItemStyle,
                  color: "#ef4444",
                  borderTop: "1px solid #e5e7eb",
                  marginTop: "4px",
                }}
                onClick={() => logoutHandler()}
              >
                Logout
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

};

export default Header;
